/**
 * Keep the first row for each key (caller should pass newest-first when desired).
 * Rows whose key is empty/null are kept individually via a unique fallback.
 */
export function dedupeByKey(rows, getKey) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const seen = new Set();
  const unique = [];
  rows.forEach((row, index) => {
    const raw = getKey(row, index);
    const key = raw == null || raw === '' ? `row:${index}` : String(raw);
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(row);
  });
  return unique;
}

function digits(value) {
  return String(value || '').replace(/\D/g, '');
}

function norm(value) {
  return String(value || '').trim().toLowerCase();
}

/** One row per member entity. Prefer database id so real members are never collapsed. */
export function dedupeMembers(rows) {
  return dedupeByKey(rows, (m, index) => {
    // Distinct DB rows must always stay distinct
    if (m.id) return `id:${m.id}`;

    const phone = digits(m.phone || m.mobile);
    if (phone.length >= 7) return `phone:${phone}`;
    const email = norm(m.email);
    if (email) return `email:${email}`;
    const gymId = norm(m.gymId || m.gym_member_id);
    if (gymId) return `gym:${gymId}`;
    const name = norm(m.name || m.full_name || m.member);
    if (name && name !== '—' && name !== 'member') return `name:${name}`;
    return `row:${index}`;
  });
}

/** One membership per member + package (keeps newest / active row). */
export function dedupeMemberships(rows) {
  const today = new Date().toISOString().slice(0, 10);

  const rank = (m) => {
    const status = norm(m.status);
    if (status === 'cancelled' || status === 'expired') return 3;
    const expiry = String(m.expiry || m.expiryDate || '').slice(0, 10);
    if (expiry && expiry < today) return 3;
    if (m.frozen || status === 'inactive') return 2;
    return 1;
  };

  const sorted = [...(rows || [])].sort((a, b) => {
    const byStatus = rank(a) - rank(b);
    if (byStatus !== 0) return byStatus;
    return String(b.start || b.startDate || b.createdAt || '').localeCompare(
      String(a.start || a.startDate || a.createdAt || ''),
    );
  });

  return dedupeByKey(sorted, (m, index) => {
    const pkg = norm(m.package || m.packageName || m.package_name);
    if (!pkg || pkg === '—') return `row:${m.id || index}`;

    if (m.memberId) return `member:${m.memberId}|pkg:${pkg}`;

    const gymId = norm(m.gymId || m.gym_member_id);
    if (gymId && gymId !== '—') return `gym:${gymId}|pkg:${pkg}`;

    const name = norm(m.member || m.memberName || m.name);
    if (name && name !== '—') return `name:${name}|pkg:${pkg}`;

    return `row:${m.id || index}`;
  });
}

/** One payment row per database record (invoice / payment ref). */
export function dedupePayments(rows) {
  return dedupeByKey(rows, (p, index) => {
    if (p.rawId) return `db:${p.rawId}`;
    if (p.invoiceId) return `invoice:${p.invoiceId}`;
    const payRef = p.id || p.paymentId;
    if (payRef) return `ref:${payRef}`;
    if (p.memberId) return `member:${p.memberId}|row:${index}`;
    const gymId = norm(p.gymId);
    if (gymId && gymId !== '—') return `gym:${gymId}|row:${index}`;
    const name = norm(p.member || p.memberName || p.name);
    if (name && name !== '—') return `name:${name}|row:${index}`;
    return `row:${index}`;
  });
}

/** One freeze row per member (prefer active; list is usually newest-first). */
export function dedupeFreezes(rows) {
  const sorted = [...(rows || [])].sort((a, b) => {
    const aActive = a.status === 'active' ? 1 : 0;
    const bActive = b.status === 'active' ? 1 : 0;
    return bActive - aActive;
  });
  return dedupeByKey(sorted, (f, index) => {
    if (f.memberId) return `id:${f.memberId}`;
    const gymId = norm(f.gymId);
    if (gymId && gymId !== '—') return `gym:${gymId}`;
    const name = norm(f.member || f.memberName || f.name);
    if (name && name !== '—') return `name:${name}`;
    return `row:${f.id || index}`;
  });
}

/** Staff-style lists: email → phone → name. */
export function dedupePeople(rows) {
  return dedupeByKey(rows, (p, index) => {
    const email = norm(p.email);
    if (email) return `email:${email}`;
    const phone = digits(p.phone || p.mobile);
    if (phone.length >= 7) return `phone:${phone}`;
    const name = norm(p.name || p.full_name);
    if (name) return `name:${name}`;
    return `row:${p.id || index}`;
  });
}

/** Locker codes should be unique. */
export function dedupeLockers(rows) {
  return dedupeByKey(rows, (l, index) => {
    const code = norm(l.id || l.lockerId || l.locker_code);
    if (code) return `code:${code}`;
    return `row:${l.dbId || l.id || index}`;
  });
}

/** Guest passes: same phone + start date = duplicate row. */
export function dedupeGuestPasses(rows) {
  return dedupeByKey(rows, (g, index) => {
    const phone = digits(g.phone);
    const start = norm(g.startDate || g.start_date);
    const name = norm(g.name || g.guest_name);
    if (phone.length >= 7 && start) return `phone-date:${phone}:${start}`;
    if (name && start) return `name-date:${name}:${start}`;
    return `row:${g.id || index}`;
  });
}

/** Named catalog items (equipment, classes, packages). */
export function dedupeByName(rows, nameKey = 'name') {
  return dedupeByKey(rows, (row, index) => {
    const name = norm(row?.[nameKey] || row?.name);
    if (name) return `name:${name}`;
    return `row:${row?.id || index}`;
  });
}

/** One attendance row per person per date (newest-first input). */
export function dedupeAttendance(rows) {
  return dedupeByKey(rows, (r, index) => {
    const date = norm(r.date || r.attendance_date).slice(0, 10);
    if (r.memberId) return `member:${r.memberId}:${date}`;
    if (r.trainerId) return `trainer:${r.trainerId}:${date}`;
    const name = norm(r.member || r.name);
    const type = norm(r.personType || 'member');
    if (name && name !== '—') return `${type}:${name}:${date}`;
    return `row:${r.id || index}`;
  });
}
